import React, { useCallback } from 'react';
import type { ProjectType as JiraProjectType } from '@jira/platform__project-types/src/project-types.tsx';
import type { GenericShowFlag, InvitePeopleProps } from '@atlassian/invite-people/types';
import { useFlagsService } from '@atlassian/jira-flags';
import ErrorFlag from '@atlassian/jira-flags/src/common/ui/components/error-flag';
import InfoFlag from '@atlassian/jira-flags/src/common/ui/components/info-flag';
import SuccessFlag from '@atlassian/jira-flags/src/common/ui/components/success-flag';
import WarningFlag from '@atlassian/jira-flags/src/common/ui/components/warning-flag';

const ISSUE_KEY_REGEXP = /(([A-Z][A-Z0-9]+)-([0-9]+))($|\?.*)/;

type ProjectType =
	| 'jira_software'
	| 'jira_service_management'
	| 'jira_work_management'
	| 'jira_product_discovery'
	| 'jira_customer_service';

export const stripUrlToIssueKey = (candidateUrl: string): string | null => {
	const match = candidateUrl.match(ISSUE_KEY_REGEXP);
	return match && match[1];
};

export const useInvitePeopleFlags = (): {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	addFlag: GenericShowFlag<any>;
} => {
	const { showFlag } = useFlagsService();
	const addFlag: InvitePeopleProps['showFlag'] = useCallback(
		({ appearance, ...flagProps }: Parameters<NonNullable<InvitePeopleProps['showFlag']>>[0]) => {
			let FlagComponent = InfoFlag;

			// oxlint-disable-next-line default-case
			switch (appearance) {
				case 'success':
				case 'normal':
					FlagComponent = SuccessFlag;
					break;

				case 'warning':
					FlagComponent = WarningFlag;
					break;

				case 'error':
					FlagComponent = ErrorFlag;
					break;
			}

			showFlag((serviceProps) => {
				const props = {
					...flagProps,
					...serviceProps,
					messageId: `invite-people-drawer.ui.content.flag-component.${appearance}.${flagProps.id}`,
					messageType: 'transactional',
				};

				// remove-when-ts7
				// oxlint-disable-next-line typescript/ban-ts-comment
				// @ts-ignore - tsgo no longer raises the underlying error here; tsc5 may still need this suppression
				return <FlagComponent {...props} />;
			});
		},
		[showFlag],
	);
	return { addFlag };
};

export const projectTypeToSubProduct = (
	projectType: ProjectType | undefined,
): 'software' | 'servicedesk' | 'core' | 'product-discovery' | undefined => {
	switch (projectType) {
		case 'jira_software':
			return 'software';
		case 'jira_service_management':
			return 'servicedesk';
		case 'jira_work_management':
			return 'core';
		case 'jira_product_discovery':
			return 'product-discovery';
		default:
			return undefined;
	}
};

// adapted from jira/src/packages/platform/services-subproduct/src/index.tsx:subProductFromProjectType
// to match the above implementation of projectTypeToSubProduct
export const jiraProjectTypeToSubProduct = (
	projectType: JiraProjectType | undefined,
): 'software' | 'servicedesk' | 'core' | 'product-discovery' | undefined => {
	switch (projectType) {
		case 'software':
			return 'software';
		case 'service_desk':
			return 'servicedesk';
		case 'business':
			return 'core';
		case 'product_discovery':
			return 'product-discovery';
		default:
			return undefined;
	}
};
